// Plan 20-06 Task 2 — Profile Achievements section (META-05 user-visible
// surface). Renders 30 badge cells; locked/unlocked driven by the
// `unlockedIds` prop (the server-authored profile.achievements list from
// Plan 20-04's wire-shape extension).
//
// Design notes:
//   - Cells render even when locked (the player needs to discover what's
//     available — DEC-030 + GDD §9.3 design intent; T-20-40 accept).
//   - Unknown ids in `unlockedIds` are ignored (T-20-39 mitigation —
//     the lookup goes against ACHIEVEMENTS_META; a future renamed-or-removed
//     id stuck in D1 history won't crash the render).
//   - Icon glyphs are placeholder geometric blocks (CONTEXT.md "Real
//     cosmetic art deferred" — the `icon-<hint>` CSS class is the future
//     hook for art swap-in).
//
// a11y: each cell is a `<button>` with an aria-label combining name +
// description + locked-state. The section uses an `<h2>` heading so
// keyboard users can navigate via headings (matches Phase 19 Plan 03
// ProfileScreen layout pattern).

import type { JSX } from 'react'
import { useIntl } from 'react-intl'

import {
  ACHIEVEMENTS_META,
  type AchievementMeta,
} from '../data/achievement-meta'

export interface AchievementsGridProps {
  /**
   * Server-authoritative list of unlocked achievement ids. Stable shape
   * from Plan 20-04 (always an array, never null). Unknown ids are
   * silently skipped by the lookup-set intersection.
   */
  readonly unlockedIds: ReadonlyArray<string>
}

export function AchievementsGrid({
  unlockedIds,
}: AchievementsGridProps): JSX.Element {
  const intl = useIntl()
  const unlockedSet = new Set(unlockedIds)
  // Count only ids that map to a known meta entry — protects the subtitle
  // count from being inflated by stale ids.
  const visibleUnlockedCount = ACHIEVEMENTS_META.filter((m) =>
    unlockedSet.has(m.id),
  ).length

  return (
    <section
      aria-labelledby="achievements-section-heading"
      data-section="profile-achievements"
      data-testid="profile-achievements-section"
      style={{
        width: '100%',
        maxWidth: '480px',
        padding: 'var(--space-4)',
        backgroundColor: 'var(--color-card)',
        borderRadius: 'var(--radius-card)',
        boxShadow: 'var(--shadow-card)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <h2
        id="achievements-section-heading"
        style={{
          margin: 0,
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-16)',
        }}
      >
        {intl.formatMessage({ id: 'profile.achievements_section.title' })}
      </h2>
      <p
        data-testid="profile-achievements-subtitle"
        style={{
          margin: 0,
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-13)',
          color: 'var(--color-text-secondary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {intl.formatMessage(
          { id: 'achievements.section_subtitle' },
          { unlocked: visibleUnlockedCount, total: ACHIEVEMENTS_META.length },
        )}
      </p>
      <ul
        data-testid="profile-achievements-grid"
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(86px, 1fr))',
          gap: '8px',
        }}
      >
        {ACHIEVEMENTS_META.map((meta) => (
          <li key={meta.id}>
            <AchievementBadge meta={meta} unlocked={unlockedSet.has(meta.id)} />
          </li>
        ))}
      </ul>
    </section>
  )
}

interface AchievementBadgeProps {
  readonly meta: AchievementMeta
  readonly unlocked: boolean
}

function AchievementBadge({
  meta,
  unlocked,
}: AchievementBadgeProps): JSX.Element {
  const intl = useIntl()
  const name = intl.formatMessage({ id: meta.i18n_key_name })
  const description = intl.formatMessage({ id: meta.i18n_key_description })
  const rarityLabel = intl.formatMessage({
    id: `achievements.rarity_${meta.rarity}`,
  })
  const lockedLabel = intl.formatMessage({ id: 'achievements.locked_state' })
  const aria = unlocked
    ? `${name}: ${description} (${rarityLabel})`
    : `${name}: ${description} — ${lockedLabel}`

  return (
    <button
      type="button"
      data-testid={`achievement-badge-${meta.id}`}
      data-unlocked={unlocked ? 'true' : 'false'}
      data-rarity={meta.rarity}
      className={`achievement-badge rarity-${meta.rarity} ${unlocked ? 'unlocked' : 'locked'}`}
      aria-label={aria}
      style={{
        width: '100%',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        background: unlocked ? 'var(--color-card)' : 'var(--color-paper-2)',
        boxShadow: unlocked ? 'var(--shadow-tile)' : 'none',
        borderRadius: 'var(--radius-tile)',
        cursor: 'pointer',
        opacity: unlocked ? 1 : 0.45,
        filter: unlocked ? 'none' : 'grayscale(0.8)',
        fontFamily: 'var(--font-family-display)',
        color: 'var(--color-text-primary)',
      }}
    >
      <span
        aria-hidden="true"
        className={`achievement-icon ${meta.icon_hint}`}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: 'var(--color-cta-primary)',
          color: 'var(--color-inkwell)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          fontWeight: 700,
        }}
      >
        {meta.id.slice(0, 1).toUpperCase()}
      </span>
      <span
        style={{
          fontWeight: 700,
          fontSize: 'var(--font-size-11)',
          lineHeight: 1.1,
          textAlign: 'center',
          minHeight: '24px',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {name}
      </span>
      <span
        data-testid={`achievement-rarity-${meta.id}`}
        style={{
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-9)',
          color: 'var(--color-text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {rarityLabel}
      </span>
    </button>
  )
}
